// SIGN-UP FUNNEL — the one source for the funnel's SHAPE, shared by every surface that renders it.
//
// WHY THIS FILE EXISTS AT ALL (card 86bb671n4, split 1/2 of 86bb597gj)
// The sign-up funnel is rendered on TWO surfaces with two different visual languages: the root app's
// live-mirroring popup (inline styles, ported pixel-for-pixel from snowbus.com) and the accounts app's
// standalone /sign-up page (design-system components). The estate's standing lesson from the mountain
// selector — eight divergent copies of one control, each green on its own suite — says the answer is
// NOT one shared component forced across two visual languages. It is one shared MACHINE: the step
// order, the field rules and the taxonomy live here exactly once, and each surface renders them in its
// own idiom. A funnel cannot silently diverge between the two apps because there is only one funnel.
//
// ⚠ THE TIER LIST IS A PLACEHOLDER — PENDING-FOUNDER. See SIGNUP_TAXONOMY below before changing it.

// ── STEP ORDER ─────────────────────────────────────────────────────────────────────────────────────
// Card-specified: credentials → account type → details.
//
// This DIVERGES from the 2023 render on purpose and the divergence is recorded rather than left
// implied. FIGMA-REFS-S36/sign-in-up.png draws "Step 1 sign up" as ONE box carrying name, email,
// phone and both password fields, with "Step 2 sign up" (the membership profile) after it — i.e. a
// two-step flow whose first step is the single box this card exists to break up. The card orders three
// steps with credentials first, so credentials-first is what is built. The render remains the
// authority for the CONTENT of each step; the card is the authority for their ORDER.
export const SIGNUP_STEPS = ["credentials", "account-type", "details"] as const;
export type SignupStep = (typeof SIGNUP_STEPS)[number];

/** Human step labels, in SIGNUP_STEPS order. Rendered as the funnel's progress rail. */
export const SIGNUP_STEP_LABELS: Record<SignupStep, string> = {
  credentials: "Your login",
  "account-type": "Account type",
  details: "Your details",
};

export const signupStepIndex = (step: SignupStep): number =>
  SIGNUP_STEPS.indexOf(step);

/** The step after `step`, or null when `step` is the last one. Never wraps. */
export function nextSignupStep(step: SignupStep): SignupStep | null {
  return SIGNUP_STEPS[signupStepIndex(step) + 1] ?? null;
}

/** The step before `step`, or null when `step` is the first one. Never wraps. */
export function prevSignupStep(step: SignupStep): SignupStep | null {
  const i = signupStepIndex(step);
  return i <= 0 ? null : (SIGNUP_STEPS[i - 1] ?? null);
}

// ── THE TAXONOMY (PENDING-FOUNDER) ────────────────────────────────────────────────────────────────
/**
 * PROVENANCE: transcribed from `FIGMA-REFS-S36/sign-in-up.png`, panel "Step 2 sign up — Choose The
 * Best Membership Profile For You", as inventoried in that folder's `DESIGN-INVENTORY.md` §1 (a
 * full-resolution read banked 2026-07-28). It is a **2023-era design, not a founder ruling** —
 * FIGMA-REFS-S36/DESIGN-INVENTORY.md says so in its own words ("propose it, do not adopt it
 * silently").
 *
 * PROVENANCE RE-VERIFIED 2026-07-30 (card 86bb671n4, continuation run). The earlier runs on this
 * card could not find FIGMA-REFS-S36 and left "verbatim" inherited on trust, because the folder is
 * NOT in this repo — it lives in the Drive kit at `<vault>/SnowGroup/02 Product/00 Engineering SOT
 * — Agent Build Kit/FIGMA-REFS-S36/`. Both sources have now been opened directly and every label
 * below was compared against them: 7 personal + 9 business + 3 business-interest = 19, matching in
 * wording AND order, plus the heading, both card blurbs and the "Select All That Apply." hint. The
 * one label worth naming is `ski-board-club`: the render is low-contrast enough at overview zoom to
 * read as "Im In A Ski/Board Club", and the banked full-resolution read is what settles it as
 * "I'm In A Ski/Board Club". Do not "fix" that apostrophe back out.
 *
 * STATUS: **PENDING-FOUNDER.** The founder is supplying a member-types image. When it lands, the
 * lists below are REPLACED and nothing else should need to change — that is the whole reason the
 * funnel reads its options from this array instead of hard-coding them into markup.
 */
export const SIGNUP_TAXONOMY = {
  status: "PENDING-FOUNDER",
  source:
    "FIGMA-REFS-S36/sign-in-up.png · Step 2 sign up (2023 design, not a ruling)",
  heading: "Choose The Best Membership Profile For You",
} as const;

export interface SignupTierOption {
  /** Stable id. Kept independent of `label` so a wording change is not a data migration. */
  id: string;
  label: string;
}

export interface SignupTierGroup {
  id: string;
  /** Rendered above the group. The render's own words. */
  heading: string;
  options: SignupTierOption[];
}

export interface SignupAccountType {
  id: "personal" | "business";
  title: string;
  /** The render's own qualifier under the title. */
  blurb: string;
  /** The render's "Select All That Apply." line above the tier list. */
  selectHint: string;
  options: SignupTierOption[];
  /** Extra groups this account type carries (business has one; personal has none). */
  groups?: SignupTierGroup[];
}

/**
 * Both cards are INDEPENDENTLY selectable in the render (its Personal box is ticked while Business is
 * not, and both tier columns are drawn live), so this is a multi-select, not a radio pair. A visitor
 * who books for themselves AND runs a rental shop is one account with both profiles.
 */
export const SIGNUP_ACCOUNT_TYPES: readonly SignupAccountType[] = [
  {
    id: "personal",
    title: "Personal Profile",
    blurb: "Select if you buy/book for you and friends",
    selectHint: "Select All That Apply.",
    options: [
      { id: "skier", label: "Skier" },
      { id: "snowboarder", label: "Snowboarder" },
      { id: "travel-rep", label: "Travel Rep" },
      { id: "student", label: "Student" },
      { id: "edge-card-holder", label: "Edge Card Holder" },
      { id: "season-pass-holder", label: "Season Pass Holder" },
      { id: "ski-board-club", label: "I'm In A Ski/Board Club" },
    ],
  },
  {
    id: "business",
    title: "Business Profile",
    blurb:
      "Select if you or your team, or a business, use this account to book for or sell to customers.",
    selectHint: "Select All That Apply.",
    options: [
      { id: "hotel-lodging", label: "Hotel / Lodging" },
      { id: "travel-agency", label: "Travel Agency" },
      { id: "travel-rep-agent", label: "Travel Rep / Agent" },
      { id: "rentals-store", label: "Rentals Store" },
      { id: "retail-store", label: "Retail Store" },
      { id: "activities", label: "Activities" },
      { id: "transportation", label: "Transportation" },
      { id: "food-beverage", label: "Food & Beverage" },
      { id: "club-association", label: "Club Or Association" },
    ],
    groups: [
      {
        id: "business-interest",
        heading: "I Am A Business Interested In:",
        options: [
          { id: "selling-online", label: "Selling Products/Services Online" },
          { id: "sponsorship-advertising", label: "Sponsorship & Advertising" },
          {
            id: "discounts-promotions",
            label: "Providing Discounts & Promotions To Travellers",
          },
        ],
      },
    ],
  },
];

/** What the account-type step collects. Keys are account-type ids; values are selected option ids. */
export interface SignupProfileSelection {
  /** Which profile cards are ticked. */
  types: string[];
  /** Selected option ids, keyed by the group they came from (account-type id or tier-group id). */
  options: Record<string, string[]>;
}

export const emptyProfileSelection = (): SignupProfileSelection => ({
  types: [],
  options: {},
});

// ── FIELD RULES ────────────────────────────────────────────────────────────────────────────────────
// Lifted from the root popup's existing SignupForm (F147, itself built to the FastCurve Techno-Functional
// "Signup" contract). Moved here rather than rewritten so splitting the box cannot quietly relax a rule
// the backend still enforces.
//
// ⚠ FOUNDER-SITE-DEFECTS-20261007-001 · PASSWORD. The F147 rule was `.{8,16}` plus lower/upper/digit/special: a
// 16-character MAXIMUM that rejected every passphrase and most password-manager output, with composition rules on
// top. The policy is now LENGTH-ONLY, matching the coordinated backend (src/helpers/passwordPolicy.ts): at least 15
// Unicode code points and at most 72 UTF-8 BYTES (bcrypt's input limit; accented letters count 2, most CJK 3, emoji 4).
// There is no composition rule (OWASP Authentication Cheat Sheet: no composition rules, 15 minimum without MFA, allow
// whitespace and Unicode). Safari's generated passwords (20 characters) and plain passphrases pass. The value is never
// trimmed and paste is never blocked. Existing logins are unaffected (this gates NEW sign-up only).
// See docs/SIGNUP-PASSWORD-POLICY-BE-DEPENDENCY.md.

/** Floor, in Unicode code points (NOT UTF-16 units: an emoji is one). */
export const SIGNUP_PASSWORD_MIN_LENGTH = 15;
/** Ceiling, in UTF-8 bytes. */
export const SIGNUP_PASSWORD_MAX_BYTES = 72;

const utf8Bytes = (v: string): number => new TextEncoder().encode(v).length;
const codePoints = (v: string): number => Array.from(v).length;

/** Human requirement line. Shown always (not only after an error) so the rule is never a surprise. */
export const SIGNUP_PASSWORD_RULE = `At least ${SIGNUP_PASSWORD_MIN_LENGTH} characters (up to ${SIGNUP_PASSWORD_MAX_BYTES} bytes). A passphrase with spaces is fine.`;

export type SignupPasswordProblem = "too-short" | "too-long";

const PASSWORD_PROBLEM_TEXT: Record<SignupPasswordProblem, string> = {
  "too-short": `at least ${SIGNUP_PASSWORD_MIN_LENGTH} characters`,
  "too-long": `no more than ${SIGNUP_PASSWORD_MAX_BYTES} bytes (accented and emoji characters use more than one)`,
};

/** Every requirement the value fails, in display order. Empty means acceptable. Never trimmed. */
export function passwordProblems(v: string): SignupPasswordProblem[] {
  const out: SignupPasswordProblem[] = [];
  if (codePoints(v) < SIGNUP_PASSWORD_MIN_LENGTH) out.push("too-short");
  if (utf8Bytes(v) > SIGNUP_PASSWORD_MAX_BYTES) out.push("too-long");
  return out;
}

export const passwordOk = (v: string): boolean => passwordProblems(v).length === 0;

/** What is missing, specifically — not just "invalid". Null when acceptable. */
export function passwordMessage(v: string): string | null {
  if (v.length === 0) return "Enter a password.";
  const problems = passwordProblems(v);
  if (problems.length === 0) return null;
  return `Password needs ${problems.map((p) => PASSWORD_PROBLEM_TEXT[p]).join(" and ")}.`;
}

/** Null when `confirm` matches. An empty confirm is "not entered yet", reported as such. */
export function confirmMessage(password: string, confirm: string): string | null {
  if (confirm.length === 0) return "Re-enter your password to confirm it.";
  return password === confirm ? null : "Passwords do not match.";
}

export const emailOk = (v: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export function emailMessage(v: string): string | null {
  if (v.trim().length === 0) return "Enter your email address.";
  return emailOk(v) ? null : "Enter a valid email address, like name@example.com.";
}

export const phoneOk = (v: string): boolean =>
  /^[0-9 +\-]{6,15}$/.test(v.trim());

export function phoneMessage(v: string): string | null {
  if (v.trim().length === 0) return "Enter a phone number for transport notifications.";
  return phoneOk(v)
    ? null
    : "Phone must be 6–15 characters: digits, spaces, + and - only.";
}

export const nameMessage = (label: string, v: string): string | null =>
  v.trim().length === 0 ? `Enter your ${label}.` : null;

export interface SignupCredentials {
  email: string;
  password: string;
  confirm: string;
}

export interface SignupDetails {
  firstName: string;
  lastName: string;
  phone: string;
}

// ── THE MEMBERSHIP-INFO POPUP'S COPY ──────────────────────────────────────────────────────────────
/**
 * The ⓘ popup's wording, held here for exactly the reason the tier list is: it is rendered on TWO
 * surfaces and duplicating it as markup is how it drifts.
 *
 * IT ALREADY HAD. Found 2026-07-30 by adversarial review of this card's own diff: the root popup read
 * "Manage, modify, & cancel trips online" and the accounts page "Manage, modify & cancel trips online"
 * (no comma), and the sign-off was one line on accounts against two on root — while the accounts file's
 * docblock claimed the text was live's own "matching exactly". One commit, two surfaces, already
 * divergent, with a comment asserting otherwise.
 *
 * ROOT'S WORDING IS THE AUTHORITY and is transcribed here VERBATIM, because root is the surface ported
 * from live and pixel-gated against it. Nothing about root's rendered text changes by moving it here —
 * it now reads the same strings from a named source. The ACCOUNTS page adopts them, which is the actual
 * correction. Each surface still styles them in its own idiom; only the words are shared.
 */
export const MEMBERSHIP_BENEFITS = [
  "A useful & cool user account",
  "Faster booking & checkout",
  "Account wallet",
  "Transaction history",
  "Connect with other shredders",
  "Save travel options for later checkout",
  "Upload ski videos and pics",
  "Manage, modify, & cancel trips online",
  "Connect with instagram",
  "A SnowClub online community profile",
] as const;

/** The sign-off, as two lines because that is how live draws it (teal over orange on root). */
export const MEMBERSHIP_SIGNOFF = {
  lead: "JOIN THE CLUB !",
  tail: "NEVER PAY FULL PRICE",
} as const;

/** Step 1 gate. Confirm must MATCH, not merely be non-empty. */
export const credentialsComplete = (c: SignupCredentials): boolean =>
  emailOk(c.email) && passwordOk(c.password) && c.password === c.confirm;

/**
 * Step 2 gate. Deliberately permissive: the render's DONE is not drawn disabled, the taxonomy is
 * PENDING-FOUNDER, and refusing to advance on a placeholder list would block sign-up on a question
 * the founder has not answered yet. Selecting nothing is a legitimate answer to "which of these are
 * you?" — so this returns true and the funnel advances. Recorded so it reads as a decision, not an
 * omission.
 */
export const accountTypeComplete = (_s: SignupProfileSelection): boolean =>
  true;

/** Step 3 gate. Phone is required — live collects it for transport notifications. */
export const detailsComplete = (d: SignupDetails): boolean =>
  !!d.firstName.trim() && !!d.lastName.trim() && phoneOk(d.phone);

// ── WHY IS CONTINUE DISABLED? ──────────────────────────────────────────────────────────────────────
// The funnel's gate used to be a bare boolean, so a refused Continue gave the visitor no way to learn
// which field was the problem (founder walk 2026-10-07). These return the SAME verdict as the gates
// above, as the list of reasons — an empty list is exactly "the gate is open". The equivalence is
// asserted in apps/root/src/lib/signup-credentials.test.ts so the two cannot drift apart.

export function credentialsBlockers(c: SignupCredentials): string[] {
  return [
    emailMessage(c.email),
    passwordMessage(c.password),
    // Only worth a line once there is something to confirm; an empty password already says so.
    c.password.length === 0 ? null : confirmMessage(c.password, c.confirm),
  ].filter((m): m is string => m !== null);
}

export function detailsBlockers(d: SignupDetails): string[] {
  return [
    nameMessage("first name", d.firstName),
    nameMessage("last name", d.lastName),
    phoneMessage(d.phone),
  ].filter((m): m is string => m !== null);
}

/** The reasons `step` cannot be left yet. Empty means `canLeaveStep` is true. */
export function stepBlockers(
  step: SignupStep,
  state: { credentials: SignupCredentials; details: SignupDetails },
): string[] {
  if (step === "credentials") return credentialsBlockers(state.credentials);
  if (step === "account-type") return [];
  return detailsBlockers(state.details);
}

/** Whether the funnel may leave `step` given what has been filled so far. */
export function canLeaveStep(
  step: SignupStep,
  state: {
    credentials: SignupCredentials;
    profile: SignupProfileSelection;
    details: SignupDetails;
  },
): boolean {
  if (step === "credentials") return credentialsComplete(state.credentials);
  if (step === "account-type") return accountTypeComplete(state.profile);
  return detailsComplete(state.details);
}
